import { Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import vconst from 'utils/helpers/structs/vconst';
import { useToast } from 'context/ToastContext';
import { useModal } from 'context/ModalContext';
import { useAuth } from 'context/AuthContext';
import { getButtonsNoYes } from 'context/helpers/helperModalContext';
import * as apiProposal from 'api/apiProposal';
import * as apiLead from 'api/apiLeads';
import SpinnerBlock from 'components/others/SpinnerBlock';
import useProposalGenerate from '../model/useProposalGenerate';
import useLeadProposalGenerations from '../model/useLeadProposalGenerations';
import useSendGenerationEmailDialog from '../../share/model/useSendGenerationEmailDialog';
import { buildDocumentMailContent } from 'utils/helpers/email/buildMailContent';
import ProposalGenerateForm from './ProposalGenerateForm';
import ProposalGenerateLogTable from './ProposalGenerateLogTable';
import type { ProposalEditFormValues } from '../types';
import SolarIcon from 'components/icons/SolarIcon';

interface ProposalGenerateTabProps {
    leadId?: number | string;
    leadProposalId?: number | string;
    leadEmail?: string;
    leadStatus?: number;
    formData: ProposalEditFormValues;
    rate: number;
    objectData?: unknown;
    onGenerated?: () => Promise<void> | void;
}

const ProposalGenerateTab = ({
    leadId,
    leadProposalId,
    leadEmail = '',
    leadStatus,
    formData,
    rate,
    objectData,
    onGenerated,
}: ProposalGenerateTabProps) => {
    const { t } = useTranslation(['Proposal', 'common']);
    const { showToast } = useToast();
    const { showDlg } = useModal();
    const { auth } = useAuth();
    const { openSendGenerationEmail } = useSendGenerationEmailDialog();
    const isAdmin = Boolean(auth?.user?.admin);
    const isLeadAccepted = Number(leadStatus) === vconst.leadAccept;
    const isLeadRejected = Number(leadStatus) === vconst.leadReject;
    const isLeadClosed = isLeadAccepted || isLeadRejected;
    const acceptLocked = isLeadClosed && !isAdmin;
    const canGenerateOnLead = !isLeadAccepted || isAdmin;
    const generateEventStatusId = isLeadAccepted ? vconst.leadAccept : vconst.leadOffer;
    const { rows, loading, refreshing, togglingId, refresh, toggleAccept } =
        useLeadProposalGenerations(leadId);
    const {
        sendToClient,
        setSendToClient,
        clientEmail,
        setClientEmail,
        generating,
        canGenerate,
        handleGenerate,
    } = useProposalGenerate({
        leadId,
        leadProposalId,
        leadEmail,
        eventStatusId: generateEventStatusId,
        formData,
        rate,
        objectData,
        onGenerated: async () => {
            await refresh();
            if (onGenerated) await onGenerated();
        },
    });

    const requestGenerate = () => {
        if (!canGenerateOnLead) return;

        if (isLeadAccepted && isAdmin) {
            const buttons = [...getButtonsNoYes(t)];
            buttons[1].handleClick = () => {
                showDlg(false);
                void handleGenerate();
            };
            showDlg({
                type: 'confirm',
                title: t('edit.generate.generateClosedLeadConfirmTitle'),
                text: t('edit.generate.generateClosedLeadConfirmText'),
                buttons,
            });
            return;
        }

        void handleGenerate();
    };

    const performToggleAccept = async (generationId: number, currentlyAccepted: boolean) => {
        const nextAccept = !currentlyAccepted;
        const targetLeadId = Number(leadId || 0);
        const generation = rows.find((row) => row.ID === generationId) || null;
        const stat = await toggleAccept(generationId, nextAccept);
        if (!stat) {
            showToast(t('common:messError'), 'danger');
            return;
        }
        if (nextAccept && targetLeadId) {
            // Погоджена: подія зі статусом «Погоджено» (статус заявки не змінюється).
            // Відхилена / інші: подія «Пропозиція готова» (для відхиленої — і зміна статусу).
            const eventStatusId = isLeadAccepted ? vconst.leadAccept : vconst.leadOffer;
            const cloudFile =
                generation?.fileStorageKey && generation?.filePublicUrl
                    ? {
                          fileName: generation.fileName || t('edit.generate.fileNameFallback'),
                          contentType: 'application/pdf',
                          fileSize: 0,
                          storageKey: generation.fileStorageKey,
                          publicUrl: generation.filePublicUrl,
                      }
                    : null;
            const eventCreated = await apiLead.addEvent(targetLeadId, {
                statusID: eventStatusId,
                eventInfo: t('edit.generate.acceptChangeEventInfo'),
                ...(cloudFile ? { eventAttachCloud: [cloudFile] } : {}),
            });
            if (!eventCreated) {
                showToast(t('common:messError'), 'warning');
            }
        }
        if (onGenerated) await onGenerated();
    };

    const handleToggleAccept = (generationId: number, currentlyAccepted: boolean) => {
        if (acceptLocked) return;

        if (isLeadClosed && isAdmin) {
            const buttons = [...getButtonsNoYes(t)];
            buttons[1].handleClick = () => {
                showDlg(false);
                void performToggleAccept(generationId, currentlyAccepted);
            };
            showDlg({
                type: 'confirm',
                title: isLeadRejected
                    ? t('edit.generate.acceptRejectedLeadConfirmTitle')
                    : t('edit.generate.acceptClosedLeadConfirmTitle'),
                text: isLeadRejected
                    ? t('edit.generate.acceptRejectedLeadConfirmText')
                    : t('edit.generate.acceptClosedLeadConfirmText'),
                buttons,
            });
            return;
        }

        void performToggleAccept(generationId, currentlyAccepted);
    };

    const handleSendEmail = (generationId: number) => {
        const targetLeadId = Number(leadId || 0);
        if (!targetLeadId) return;
        const generation = rows.find((row) => row.ID === generationId) || null;
        const fileUrl = String(generation?.filePublicUrl || '').trim();

        openSendGenerationEmail({
            defaultEmail: leadEmail || clientEmail || '',
            onSubmit: async ({ email, emailBody }) => {
                if (!fileUrl || !generation) {
                    showToast(t('common:messError'), 'danger');
                    return false;
                }
                const mailContent = await buildDocumentMailContent({
                    type: 'proposal',
                    documentName: generation.proposalName || generation.fileName || 'proposal',
                    fileUrl,
                    emailBody,
                });
                if (!mailContent) {
                    showToast(t('common:messError'), 'danger');
                    return false;
                }
                const stat = await apiProposal.sendLeadProposalGenerationEmail(targetLeadId, generationId, {
                    email,
                    emailBody,
                    mailContent,
                });
                if (!stat) {
                    showToast(t('common:messError'), 'danger');
                    return false;
                }
                showToast(t('edit.generate.sendDialog.success'), 'success');
                await refresh();
                return true;
            },
        });
    };

    const closedLeadHint = isLeadAccepted
        ? isAdmin
            ? t('edit.generate.leadAcceptedAdminHint')
            : t('edit.generate.leadAcceptedLockedHint')
        : isLeadRejected
          ? isAdmin
              ? t('edit.generate.leadRejectedAdminHint')
              : t('edit.generate.leadRejectedLockedHint')
          : null;

    return (
        <div className="proposal-generate-tab">
            {canGenerateOnLead ? (
                <ProposalGenerateForm
                    sendToClient={sendToClient}
                    setSendToClient={setSendToClient}
                    clientEmail={clientEmail}
                    setClientEmail={setClientEmail}
                    generating={generating}
                    canGenerate={canGenerate}
                    onGenerate={requestGenerate}
                />
            ) : null}

            {closedLeadHint ? (
                <div className={`text-muted fs-12 mb-3${canGenerateOnLead ? ' mt-3' : ''}`}>
                    {closedLeadHint}
                </div>
            ) : null}

            <div className={canGenerateOnLead || closedLeadHint ? 'mt-4' : ''}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">{t('edit.generate.logTitle')}</h6>
                    <Button
                        size="sm"
                        variant="light"
                        onClick={() => void refresh()}
                        disabled={loading || refreshing || generating}
                    >
                        <SolarIcon name="arrow-clockwise" className="me-1" />
                        {t('edit.generate.logRefresh')}
                    </Button>
                </div>

                <div className="position-relative">
                    <SpinnerBlock onlyBlur={true} show={refreshing} />
                    {loading ? (
                        <div className="text-muted fs-12 d-flex align-items-center gap-2">
                            <Spinner animation="border" size="sm" />
                            {t('edit.generate.logLoading')}
                        </div>
                    ) : rows.length ? (
                        <ProposalGenerateLogTable
                            rows={rows}
                            togglingId={togglingId}
                            generating={generating || refreshing}
                            acceptLocked={acceptLocked}
                            onToggleAccept={handleToggleAccept}
                            onSendEmail={handleSendEmail}
                        />
                    ) : (
                        <div className="text-muted fs-12">{t('edit.generate.logEmpty')}</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProposalGenerateTab;
